import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact, { BugsnagErrorBoundary } from '@bugsnag/plugin-react';
import BugsnagPerformance from '@bugsnag/browser-performance';

interface BugsnagContextType {
  setBugsnagUser: (id: string, email: string, name: string) => void;
  setBugsnagOrganisation: (id: string, name: string) => void;
  ErrorBoundary: BugsnagErrorBoundary;
}

interface BugsnagProviderProps {
  children: ReactNode;
}

const BugsnagContext = createContext<BugsnagContextType | undefined>(undefined);
export const BugsnagProvider: React.FC<BugsnagProviderProps> = ({ children }) => {
  const [isBugsnagInitialized, setIsBugsnagInitialized] = useState(false);

  useEffect(() => {
    // Don't initialize on localhost, to prevent event flooding.
    if (isBugsnagInitialized || process.env.NEXT_PUBLIC_STAGE === 'local') {
      return;
    }

    Bugsnag.start({
      apiKey: process.env.NEXT_PUBLIC_BUGSNAG_API_KEY,
      releaseStage: process.env.NEXT_PUBLIC_STAGE,
      appVersion: process.env.NEXT_PUBLIC_APP_VERSION,
      plugins: [new BugsnagPluginReact()],
      onError: (event) => {
        const firstError = event.errors?.[0];
        const message = firstError?.errorMessage ?? '';
        const errorClass = firstError?.errorClass ?? '';
        if (errorClass === 'OrganisationNotFoundError') return false;
        if (errorClass === 'NotAuthorizedException' && message.includes('Access Token has been revoked')) {
          return false;
        }
        if (
          message.includes('ResizeObserver loop') ||
          message === 'Cancel rendering route' ||
          message.includes('Invalid regular expression: invalid group specifier name')
        ) {
          return false;
        }
        return true;
      }
    });
    BugsnagPerformance.start({ apiKey: process.env.NEXT_PUBLIC_BUGSNAG_API_KEY });
    setIsBugsnagInitialized(true);
  }, []);

  const setBugsnagUser = (id: string, email: string = '-', name: string = 'Anonymous') => {
    // Don't initialize on localhost, to prevent event flooding.
    if (!isBugsnagInitialized || process.env.NEXT_PUBLIC_STAGE === 'local') {
      return;
    }

    Bugsnag.setUser(id, email, name);
  };

  const setBugsnagOrganisation = (id: string, name: string) => {
    // Don't initialize on localhost, to prevent event flooding.
    if (!isBugsnagInitialized || process.env.NEXT_PUBLIC_STAGE === 'local') {
      return;
    }

    Bugsnag.addMetadata('organisation', { id, name });
  };

  const ErrorBoundary = useMemo(() => {
    if (isBugsnagInitialized) {
      return Bugsnag.getPlugin('react')?.createErrorBoundary(React);
    }

    // Provide a fallback error boundary component
    return ({ children: childrenDeep }: { children: ReactNode }) => <>{childrenDeep}</>;
  }, [isBugsnagInitialized]);

  const contextValue = useMemo(
    () => ({
      setBugsnagUser,
      setBugsnagOrganisation,
      ErrorBoundary
    }),
    [setBugsnagUser, setBugsnagOrganisation, ErrorBoundary]
  );

  return <BugsnagContext.Provider value={contextValue}>{children}</BugsnagContext.Provider>;
};

export const useBugsnag = (): BugsnagContextType => {
  const context = useContext(BugsnagContext);
  if (!context) {
    throw new Error('useBugsnag must be used within a BugsnagProvider');
  }
  return context;
};
